import { Box, Button, Typography } from "@mui/material";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { useExportTransactionMutation } from "src/entities/transactions/api/api";
import { IconComponent } from "src/shared/components";
import { Can } from "src/shared/lib/rback/Can";
import { EIconNames } from "src/shared/types/iconNames";
import { TransactionDrawerSummary } from "../../../entities/transactions/model/types";
import { useTransactionsFilters } from "../../../features/transactionsFilters/model/useTransactionsFilters";
import { useTransactionsQuery } from "../../../features/transactionsFilters/model/useTransactionsQuery";
import { TransactionDrawer } from "../../../widgets/transactionsDetailsDrawer/ui/TransactionDrawer";
import { TransactionsTable } from "../../../widgets/transactionsTable/ui/TransactionsTable";
import { getTransactionsExportQuery } from "../helper/getTransactionsExportQuery";

export function TransactionsPage() {
  const {
    query,
    filters,
    sortModel,
    onSortModelChange,
    onPaginationModelChange,
    onResetFilter,
    onFilter,
    search,
    onSearch,
  } = useTransactionsFilters();

  const { data, isFetching, refetch } = useTransactionsQuery(query);
  const [exportTransaction, { isLoading: isExportLoading }] =
    useExportTransactionMutation();

  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionDrawerSummary | null>(null);

  const handleOpenDrawer = useCallback((row: TransactionDrawerSummary) => {
    setSelectedTransaction(row);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedTransaction(null);
    refetch();
  }, [refetch]);

  const exportQuery = useMemo(() => getTransactionsExportQuery(query), [query]);

  const handleExport = useCallback(async () => {
    try {
      const response = await exportTransaction(exportQuery).unwrap();

      enqueueSnackbar(
        response?.data?.message ??
          "Экспорт поставлен в очередь. Файл будет отправлен на почту.",
        {
          variant: "success",
          handleClose: closeSnackbar,
        },
      );
    } catch {
      enqueueSnackbar("Не удалось запустить экспорт транзакций", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }, [exportTransaction, exportQuery]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 3,
        }}
      >
        <Typography variant="h6">Транзакции</Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 3,
            ml: "auto",
          }}
        >
          <Can perform="transactions.export">
            <Button
              onClick={handleExport}
              startIcon={<IconComponent iconname={EIconNames.DOWNLOAD} />}
              variant="outlined"
              disabled={isExportLoading}
            >
              Экспорт транзакций
            </Button>
          </Can>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <TransactionsTable
          rows={data.data}
          loading={isFetching}
          rowCount={data.meta.total}
          paginationModel={{
            page: query.page ? Number(query.page) - 1 : 0,
            pageSize: query.per_page ? Number(query.per_page) : 50,
          }}
          pageSizeOptions={[25, 50, 100]}
          onPaginationModelChange={onPaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          filters={filters}
          onFilter={onFilter}
          onResetFilter={onResetFilter}
          onOpenDrawer={handleOpenDrawer}
          search={search ?? ""}
          onSearch={onSearch}
        />
      </Box>

      <TransactionDrawer
        open={Boolean(selectedTransaction)}
        transactionId={selectedTransaction?.id ?? null}
        summary={selectedTransaction}
        onClose={handleCloseDrawer}
      />
    </Box>
  );
}
